<template>
  <v-sheet class="pa-4 h-100">
    <v-sheet outlined class="rounded overflow-hidden h-100 d-flex flex-column">
      <div
        class="pa-2 d-flex align-center justify-space-between"
        style="gap: 8px"
        :class="{
          'flex-wrap': $vuetify.breakpoint.xs,
        }">
       
        <v-spacer v-if="!$vuetify.breakpoint.xs"></v-spacer>
        <v-sheet :width="$vuetify.breakpoint.xs ? '100%' : 250">
          <v-text-field
            hide-details
            outlined
            v-model="options.search"
            dense
            class="search-bar"
            placeholder="Search in invited users..."
            clearable
            prepend-inner-icon="mdi-magnify"></v-text-field>
        </v-sheet>
      </div>
      <v-data-table
        class="elevation-0 pa-2 rounded-0 d-flex flex-column table-sticky custom-table"
        :footer-props="{
          showFirstLastPage: true,
          firstIcon: 'mdi-chevron-double-left',
          lastIcon: 'mdi-chevron-double-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
          itemsPerPageText: 'Row/Page',
          itemsPerPageOptions: [15, 30, 50, 100, 200, 300, 400, 500, -1],
        }"
        :headers="userHeaders"
        :items="entries"
        :options.sync="options"
        :loading="loading"
        :server-items-length="total">
        <template v-slot:[`item.firstName`]="{ item }">
          <div class="d-inline-flex">
            <user-avatar :user="item" hide-roles disable-open-link></user-avatar>
          </div>
        </template>
        <template v-slot:[`item.email`]="{ item }">
          <a :href="'mailto:' + item.email">{{ item.email }}</a>
        </template>

        <template v-slot:[`item.roles`]="{ item }">
          <v-row style="margin: 5px 0px">
            <v-chip
              style="font-size: 11px; font-weight: 600"
              v-for="role in item.roles"
              :key="role.name"
              label
              x-small
              color="deep-purple"
              text-color="white"
              :ripple="false"
              link>
              {{ role.name }}
            </v-chip>
          </v-row>
        </template>
        <template v-slot:[`item.isActivated`]="{ item }">
          <span v-if="item.isActivated" class="green--text">
            <v-icon left>mdi-play</v-icon> Activated
          </span>
          <span v-else class="d-flex align-center opacity-40">
            <i class="fad fa-face-sleeping fs-18px mr-2"></i>Not Activated!
          </span>
        </template>

        <template v-slot:[`item.createDate`]="{ item }">
          {{ $formatDate(item.createDate) }}
        </template>
        <template v-slot:[`item.updateDate`]="{ item }">
          {{ $formatDate(item.updateDate) }}
        </template>

        <template v-slot:[`item.isExpired`]="{ item }">
          <v-chip
            small
            label
            :color="item.isExpired ? 'error' : 'success'"
            :text-color="'white'"
            class="font-weight-medium">
            <v-icon small left>{{ item.isExpired ? 'mdi-clock-alert-outline' : 'mdi-check-circle-outline' }}</v-icon>
            {{ item.isExpired ? 'Expired' : 'Valid' }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu dense offset-x right attach>
            <template v-slot:activator="{ attrs, on }">
              <v-btn icon elevation="0" v-bind="attrs" v-on="on">
                <v-icon size="16px">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list class="more-options-menu" dense>
              <v-list-item @click="resend(item.id)" dense>
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Resend Invitation</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="viewInvitationCode(item)" dense>
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="primary">mdi-link-variant</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>View Invitation Code</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <!-- <v-list-item @click="del(item.id)" dense>
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="error">mdi-delete-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Delete Invitation</v-list-item-title>
                </v-list-item-content>
              </v-list-item> -->
            </v-list>
          </v-menu>
        </template>

        <template v-slot:no-data>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">No Data Available!</p>
          </div>
        </template>

        <template v-slot:loading>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">Loading Users...</p>
          </div>
        </template>
      </v-data-table>
      <EditUser ref="editUser"></EditUser>
      
      <!-- Invitation Code Modal -->
      <v-dialog v-model="showInvitationCodeModal" max-width="70vw">
        <v-card>
          <v-card-title class="headline primary--text">
            <v-icon left color="primary">mdi-link-variant</v-icon>
            Invitation Link
          </v-card-title>
          <v-divider></v-divider>
          
          <v-card-text class="pt-4">
            <p class="mb-2">Share this link with the user to complete their registration:</p>
            <v-alert
              outlined
              color="primary"
              class="mb-3 invitation-url"
              dense
            >
              <div class="d-flex align-center invitation-url-container">
                <div class="invitation-url-text">{{ currentInvitationUrl }}</div>
                <v-btn
                  icon
                  small
                  color="primary"
                  class="ml-2 flex-shrink-0"
                  @click="copyInvitationUrl"
                  title="Copy to clipboard"
                >
                  <v-icon>mdi-content-copy</v-icon>
                </v-btn>
              </div>
            </v-alert>
           
          </v-card-text>
          
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              @click="showInvitationCodeModal = false"
            >
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-sheet>
  </v-sheet>
</template>

<script>
  import UserAvatar from '@Share/components/UserAvatar.vue';
  import userHeaders from '@User/config/management-userInvitation.headers';
  import { mapGetters } from 'vuex';
  import EditUser from '@User/components/EditUser.vue';
  import permissions from '@Share/config/permissions';
  import { $formatDate } from '@Utils';
  import userService from '@User/service/userInvitation.service';
  import { log, warn, error } from '@/utils/debugLogger';

  export default {
    name: 'management-invite-user-list',
    props: {
      companyId: {
        type: [String, Number],
        default: null
      }
    },
    data() {
      return {
        userHeaders,
        activeUsers: false,
        permissions,
        showInvitationCodeModal: false,
        currentInvitationCode: '',
        currentInvitationUrl: '',
      };
    },
    computed: {
      ...mapGetters('USER_INVITATION', {
        loading: 'GET_LOADING',
        entries: 'GET_ENTRIES',
        total: 'GET_TOTAL',
      }),
      options: {
        get() {
          return this.$store.getters['USER_INVITATION/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('USER_INVITATION/SET_OPTIONS', val);
        },
      },
    },
    methods: {
      $formatDate,
      getInvitations() {
        if (this.companyId) {
          this.$store.dispatch('USER_INVITATION/GET_BY_COMPANY_ID', this.companyId);
        } else {
          this.$store.dispatch('USER_INVITATION/GET');
        }
      },
      update(userItemId = null) {
        this.$refs.editUser.open(userItemId, true, this.companyId);
      },
      resend(id) {
        this.$dialog.info({
          text: 'Are You Sure You Want to Resend This Invitation?',
          title: 'Resend Invitation',
          color: 'primary',
          attach: true,
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                return this.$store.dispatch('USER_INVITATION/RESEND', id);
              },
            },
          },
        });
      },
      accept(id) {
        this.$store.dispatch('USER_INVITATION/ACCEPT', id);
      },
      viewInvitationCode(item) {
        // Get invitation URL from API
        this.currentInvitationUrl = '';
        this.showInvitationCodeModal = true;
        
        // Call the service to get the invitation URL
        userService.getInvitationUrl(this.companyId, item.id)
          .then(response => {
            if (response && response.data) {
              this.currentInvitationUrl = response.data?.url;
            } else {
              this.$dialog.notify.error('Could not retrieve invitation URL', {
                position: 'top-right',
                timeout: 3000,
              });
            }
          })
          .catch(error => {
            error('Error fetching invitation URL:', error);
            this.$dialog.notify.error('Error retrieving invitation URL', {
              position: 'top-right',
              timeout: 3000,
            });
          });
      },
      
      copyInvitationUrl() {
        this.copyToClipboard(this.currentInvitationUrl);
        this.$dialog.notify.success('Invitation URL copied to clipboard!', {
          position: 'top-right',
          timeout: 2000,
        });
      },
      
      copyInvitationCode() {
        this.copyToClipboard(this.currentInvitationCode);
        this.$dialog.notify.success('Invitation code copied to clipboard!', {
          position: 'top-right',
          timeout: 2000,
        });
      },
      
      copyToClipboard(text) {
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(text)
            .catch(err => {
              error('Failed to copy text: ', err);
              this.fallbackCopyToClipboard(text);
            });
        } else {
          this.fallbackCopyToClipboard(text);
        }
      },
      
      fallbackCopyToClipboard(text) {
        try {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();
          const successful = document.execCommand('copy');
          document.body.removeChild(textarea);
          
          if (!successful) {
            error('Fallback copy method failed');
          }
        } catch (err) {
          error('Fallback copy method failed: ', err);
        }
      },
      
      del(id) {
        this.$dialog.error({
          text: 'Are You Sure You Want to Delete This Invitation?',
          title: 'Delete Invitation',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                return this.$store.dispatch('USER_INVITATION/DELETE', id);
              },
            },
          },
        });
      },
    },
    watch: {
      options: {
        deep: true,
        handler() {
          this.getInvitations();
        },
      },
      companyId: {
        immediate: true,
        handler() {
          if (this.companyId) {
            this.getInvitations();
          }
        }
      }
    },
    components: {
      UserAvatar,
      EditUser,
    },
  };
</script>

<style lang="scss" scoped>
.invitation-url,
.invitation-code {
  position: relative;
  font-family: monospace;
  font-size: 14px;
  padding: 12px;
  margin-bottom: 16px;
  border-radius: 4px;
  background-color: rgba(var(--v-primary-base), 0.05);
  border: 1px solid rgba(var(--v-primary-base), 0.2);
  word-break: break-all;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(var(--v-primary-base), 0.08);
  }

  .v-btn {
    opacity: 0.7;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  }
}

.headline {
  font-size: 1.25rem !important;
  font-weight: 500;
  line-height: 2rem;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.invitation-url-container {
  width: 100%;
  min-width: 0;
}

.invitation-url-text {
  flex: 1;
  width: 300px;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding: 4px 0;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;

    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
}
</style>
